// src/components/cabling/CablingCompletionForecast.jsx
import React from 'react';
import { COLORS } from '../../constants';

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TargetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const SpeedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export default function CablingCompletionForecast({ copperData, fibreData }) {
  const totalCopperQty = copperData?.reduce((sum, c) => sum + (Number(c.qty) || 0), 0) || 0;
  const totalFibreQty = fibreData?.reduce((sum, f) => sum + (Number(f.qty) || 0), 0) || 0;
  
  const copperInstalled = copperData?.reduce((sum, c) => sum + (Number(c.roughedIn) || 0), 0) || 0;
  const fibreInstalled = fibreData?.reduce((sum, f) => sum + (Number(f.completed) || 0), 0) || 0;
  
  const copperRemaining = totalCopperQty - copperInstalled;
  const fibreRemaining = totalFibreQty - fibreInstalled;
  
  // Dynamic rate calculation based on actual progress
  const copperRatePerDay = copperInstalled > 0 ? Math.max(20, Math.floor(copperInstalled / 7)) : 50;
  const fibreRatePerDay = fibreInstalled > 0 ? Math.max(10, Math.floor(fibreInstalled / 7)) : 30;
  
  const copperDaysRemaining = copperRemaining > 0 ? Math.ceil(copperRemaining / copperRatePerDay) : 0;
  const fibreDaysRemaining = fibreRemaining > 0 ? Math.ceil(fibreRemaining / fibreRatePerDay) : 0;
  
  const today = new Date();
  const copperCompletion = new Date(today);
  copperCompletion.setDate(today.getDate() + copperDaysRemaining);
  const fibreCompletion = new Date(today);
  fibreCompletion.setDate(today.getDate() + fibreDaysRemaining);
  
  const forecasts = [
    {
      name: 'Copper',
      remaining: copperRemaining,
      days: copperDaysRemaining,
      date: copperCompletion,
      color: COLORS.primary,
      rate: copperRatePerDay,
    },
    {
      name: 'Fibre',
      remaining: fibreRemaining,
      days: fibreDaysRemaining,
      date: fibreCompletion,
      color: COLORS.cyan,
      rate: fibreRatePerDay,
    },
  ];
  
  const maxDays = Math.max(copperDaysRemaining, fibreDaysRemaining, 1);
  
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        border: `1px solid ${COLORS.line}`,
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <CalendarIcon />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>Completion Forecast</h3>
        </div>
        <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: 0 }}>
          Estimated based on current installation velocity
        </p>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {forecasts.map((forecast) => (
          <div key={forecast.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{forecast.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: COLORS.surface, padding: '2px 8px', borderRadius: 12 }}>
                  <SpeedIcon />
                  <span style={{ fontSize: 10, color: COLORS.textSecondary }}>{forecast.rate} units/day</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: forecast.color }}>
                  {forecast.days === 0 ? 'Complete' : `${forecast.days} days`}
                </div>
                {forecast.remaining > 0 && (
                  <div style={{ fontSize: 10, color: COLORS.textSecondary }}>
                    {forecast.remaining.toLocaleString()} units remaining
                  </div>
                )}
              </div>
            </div>
            
            {forecast.remaining > 0 && (
              <>
                <div
                  style={{
                    height: 8,
                    background: COLORS.line,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      width: `${(forecast.days / maxDays) * 100}%`,
                      height: '100%',
                      background: forecast.color,
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </div>
                
                <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
                  Est. completion: {forecast.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </>
            )}
            
            {forecast.remaining === 0 && (
              <div style={{ fontSize: 11, color: COLORS.success, fontWeight: 600 }}>
                ✓ Installation complete
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div
        style={{
          marginTop: 20,
          padding: 12,
          background: COLORS.successLight,
          borderRadius: 8,
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
          <TargetIcon />
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.success }}>Target Completion Date</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.text }}>
          {Math.max(copperDaysRemaining, fibreDaysRemaining) === 0 
            ? 'All Complete!' 
            : `${Math.max(copperDaysRemaining, fibreDaysRemaining)} days remaining`}
        </div>
      </div>
    </div>
  );
}